import {
  type ImmunizationDose,
  type ImmunizationGroup,
  immunizationGroupRecords,
  isVaccineScheduleGroup,
} from '@liza/fhir-utils';
import { cn } from '@liza/ui/utils';
import { format, parseISO } from 'date-fns';
import { ChevronDownIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '../../utils/date';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { useImmunizationGroupLabel } from '../../hooks/useImmunizationGroupLabel';
import { type PromotionStatus, RecordListItem } from './RecordListItem';

export type ImmunizationGroupRowProps = {
  group: ImmunizationGroup;
  onSelectImmunization?: (id: string) => void;
  selectedImmunizationId?: string;
  onPromotionClick?: (id: string) => void;
  getPromotionStatus?: (id: string) => PromotionStatus;
};

const formatDay = (day: string): string => format(parseISO(day), DATE_FORMATS.DATE);

/** One vaccine group: a collapsed header with dose count and most recent date that expands to the
 * dated doses. Opens on first render when it holds the selected record, merged duplicates included. */
export const ImmunizationGroupRow = ({
  group,
  onSelectImmunization,
  selectedImmunizationId,
  onPromotionClick,
  getPromotionStatus,
}: ImmunizationGroupRowProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const groupLabel = useImmunizationGroupLabel();
  const containsSelected =
    selectedImmunizationId !== undefined &&
    immunizationGroupRecords(group).some((record) => record.id === selectedImmunizationId);
  const [open, setOpen] = useState(containsSelected);

  // A later selection (the sidebar navigating to another dose while the list stays mounted) must
  // open its group too; a manual collapse otherwise stays as the user left it.
  useEffect(() => {
    if (containsSelected) setOpen(true);
  }, [containsSelected, selectedImmunizationId]);

  const label = groupLabel(group);
  const count =
    group.kind === 'history'
      ? t('immunityByHistory')
      : isVaccineScheduleGroup(group)
        ? t('immunizationDoses', { count: group.doses.length })
        : t('immunizationRecordCount', { count: group.doses.length });
  const description = [count, group.mostRecent ? t('mostRecentDose', { date: formatDay(group.mostRecent) }) : undefined]
    .filter(Boolean)
    .join(' · ');

  const isDoseSelected = (dose: ImmunizationDose): boolean =>
    selectedImmunizationId !== undefined &&
    [dose.record, ...dose.merged].some((record) => record.id === selectedImmunizationId);

  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid={`immunization-group-${group.key}`}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-auto w-full items-center justify-between gap-2 px-3 py-2 text-left font-normal"
          aria-label={t('showDoses', { name: label })}
          data-testid="immunization-group-trigger"
        >
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium">{label}</span>
            <span className="text-xs text-muted-foreground">{description}</span>
          </span>
          <ChevronDownIcon
            className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-4 space-y-1 border-l pl-2">
          {group.doses.map((dose) => (
            <RecordListItem
              key={dose.record.id}
              testId="immunization-dose"
              title={dose.shortName ?? t('unknownImmunization')}
              description={dose.date ? formatDay(dose.date) : t('unknown')}
              onClick={onSelectImmunization ? (): void => onSelectImmunization(dose.record.id) : undefined}
              selected={isDoseSelected(dose)}
              onPromotionClick={onPromotionClick ? (): void => onPromotionClick(dose.record.id) : undefined}
              promotionStatus={getPromotionStatus?.(dose.record.id)}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
